import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, shopApi, getShopSession, uploadShopFile, fileUrl } from "@/lib/api";
import { useShopIdleLogout } from "@/hooks/useShopIdleLogout";
import { ShopHeader } from "@/components/ShopHeader";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { FileUpload } from "@/components/FileUpload";
import {
  Users, DollarSign, ScrollText, ShieldCheck, Plus, Pencil, Trash2, Save, ChevronLeft, KeyRound, Coins,
} from "lucide-react";

function fmtTHB(n) { return `฿${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
function fmtTHBn(n) { return `฿${Math.round(Number(n || 0)).toLocaleString("en-US")}`; }

function ChangeMyPin({ slug }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await shopApi(slug).post(`/shops/${slug}/change-my-pin`, { current_pin: cur, new_pin: nw });
      toast.success(t("hub.pin_changed"));
      setOpen(false); setCur(""); setNw("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="change-my-pin-button">
        <KeyRound className="w-3.5 h-3.5 mr-1.5" /> {t("hub.change_my_pin")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>{t("hub.change_my_pin")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="caption-label">{t("common.current_pin")}</Label>
              <Input inputMode="numeric" maxLength={4} value={cur} onChange={(e) => setCur(e.target.value.replace(/\D/g, ""))} data-testid="change-pin-current" />
            </div>
            <div>
              <Label className="caption-label">{t("common.new_pin")}</Label>
              <Input inputMode="numeric" maxLength={4} value={nw} onChange={(e) => setNw(e.target.value.replace(/\D/g, ""))} data-testid="change-pin-new" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={busy || cur.length !== 4 || nw.length !== 4} onClick={submit} className="bg-emerald-800 hover:bg-emerald-900" data-testid="change-pin-submit">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ---------------- Staff tab ---------------- */
function StaffTab({ slug, onAuthErr }) {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const r = await shopApi(slug).get(`/shops/${slug}/staff`);
      setRows(r.data.staff);
    } catch (e) {
      if (e.response?.status === 401) onAuthErr?.();
      else toast.error(e.response?.data?.detail || "Failed to load staff");
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.working_name}?`)) return;
    await shopApi(slug).delete(`/shops/${slug}/staff/${s.id}`);
    toast.success("Staff deleted");
    load();
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-serif text-2xl">{t("hub.staff")}</h2>
          <p className="text-sm text-stone-500">{t("hub.staff_desc")}</p>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-emerald-800 hover:bg-emerald-900" data-testid="add-staff-button">
          <Plus className="w-4 h-4 mr-1" /> {t("staff_form.add_cta")}
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left border-b border-stone-200">
            <tr>
              <th className="py-2 pr-3 caption-label">Working name</th>
              <th className="py-2 pr-3 caption-label">Legal name</th>
              <th className="py-2 pr-3 caption-label">Phone</th>
              <th className="py-2 pr-3 caption-label">Email</th>
              <th className="py-2 pr-3 caption-label text-right">Commission</th>
              <th className="py-2 pr-3 caption-label text-right">Guaranteed daily pay</th>
              <th className="py-2 pr-3 caption-label">Status</th>
              <th className="py-2 pr-3 caption-label text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="staff-table-body">
            {rows.map(s => (
              <tr key={s.id} className="border-b border-stone-100" data-testid={`staff-row-${s.id}`}>
                <td className="py-2 pr-3">
                  <div className="flex items-center gap-3">
                    {s.photo_path ? <img src={fileUrl(s.photo_path)} className="w-8 h-8 rounded-full object-cover ring-1 ring-stone-200" alt="" /> : <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center text-xs font-mono">{(s.working_name || "?").slice(0,2).toUpperCase()}</div>}
                    <span className="font-medium">{s.working_name}</span>
                  </div>
                </td>
                <td className="py-2 pr-3">{s.name}</td>
                <td className="py-2 pr-3 font-mono text-xs">{s.telephone || "—"}</td>
                <td className="py-2 pr-3">{s.email || "—"}</td>
                <td className="py-2 pr-3 text-right font-mono tabular">{Math.round((s.commission_rate || 0) * 100)}%</td>
                <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHBn(s.guaranteed_daily_pay_thb || 0)}</td>
                <td className="py-2 pr-3">{s.is_enabled ? <span className="text-emerald-800 text-xs">Enabled</span> : <span className="text-stone-400 text-xs">Disabled</span>}</td>
                <td className="py-2 pr-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(s); setOpen(true); }} data-testid={`edit-staff-${s.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(s)} data-testid={`delete-staff-${s.id}`}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-stone-500">No staff yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <StaffForm slug={slug} open={open} onOpenChange={setOpen} initial={editing} onSaved={load} />
    </>
  );
}

function StaffForm({ slug, open, onOpenChange, initial, onSaved }) {
  const { t } = useLang();
  const isEdit = !!initial?.id;
  const [name, setName] = useState("");
  const [working, setWorking] = useState("");
  const [pin, setPin] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [comm, setComm] = useState(40);
  const [guaranteedPay, setGuaranteedPay] = useState(0);
  const [enabled, setEnabled] = useState(true);
  const [photo, setPhoto] = useState(null);
  const [cert, setCert] = useState(null);
  const [reg, setReg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setName(initial?.name || "");
    setWorking(initial?.working_name || "");
    setPin("");
    setPhone(initial?.telephone || "");
    setEmail(initial?.email || "");
    setComm(initial ? Math.round((initial.commission_rate || 0) * 100) : 40);
    setGuaranteedPay(initial?.guaranteed_daily_pay_thb || 0);
    setEnabled(initial?.is_enabled ?? true);
    setPhoto(initial?.photo_path || null);
    setCert(initial?.thai_cert_path || null);
    setReg(initial?.therapist_reg_path || null);
    setErr("");
  }, [open, initial]);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const payload = {
        name, working_name: working, telephone: phone, email: email || undefined,
        commission_rate: Number(comm) / 100,
        guaranteed_daily_pay_thb: Number(guaranteedPay) || 0,
        is_enabled: enabled,
        photo_path: photo, thai_cert_path: cert, therapist_reg_path: reg,
      };
      if (pin) payload.pin = pin;
      if (isEdit) {
        await shopApi(slug).put(`/shops/${slug}/staff/${initial.id}`, payload);
      } else {
        if (!pin) { setBusy(false); setErr(t("staff_form.pin_required")); return; }
        await shopApi(slug).post(`/shops/${slug}/staff`, payload);
      }
      toast.success("Saved");
      onSaved?.(); onOpenChange(false);
    } catch (e) {
      setErr(e.response?.data?.detail || t("staff_form.generic_error"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="staff-form-modal">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{isEdit ? t("staff_form.title_edit") : t("staff_form.title_add")}</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="caption-label">{t("staff_form.legal_name")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="staff-form-name" />
          </div>
          <div>
            <Label className="caption-label">{t("staff_form.working_name")}</Label>
            <Input value={working} onChange={(e) => setWorking(e.target.value)} data-testid="staff-form-working" />
          </div>
          <div>
            <Label className="caption-label">{t("staff_form.pin")} {isEdit && <span className="text-stone-400">{t("staff_form.pin_blank_keep")}</span>}</Label>
            <Input inputMode="numeric" maxLength={4} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} data-testid="staff-form-pin" />
          </div>
          <div>
            <Label className="caption-label">{t("staff_form.commission")}</Label>
            <Input type="number" min="0" max="100" value={comm} onChange={(e) => setComm(e.target.value)} data-testid="staff-form-commission" />
          </div>
          <div>
            <Label className="caption-label">{t("staff_form.guaranteed_pay")}</Label>
            <Input type="number" min="0" step="1" value={guaranteedPay} onChange={(e) => setGuaranteedPay(e.target.value)} data-testid="staff-form-guaranteed-pay" />
          </div>
          <div>
            <Label className="caption-label">{t("staff_form.phone")}</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="staff-form-phone" />
          </div>
          <div>
            <Label className="caption-label">{t("staff_form.email")}</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="staff-form-email" />
          </div>
          <div className="md:col-span-2">
            <label className="flex items-center gap-2 text-sm text-stone-700">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="staff-form-enabled" />
              {t("staff_form.allow_signin")}
            </label>
          </div>
          <FileUpload label={t("staff_form.photo_id")} value={photo} onChange={setPhoto} uploader={(f) => uploadShopFile(slug, f, "staff")} testId="staff-form-photo" />
          <FileUpload label={t("staff_form.cert")} value={cert} onChange={setCert} uploader={(f) => uploadShopFile(slug, f, "staff")} testId="staff-form-cert" />
          <div className="md:col-span-2">
            <FileUpload label={t("staff_form.registration")} value={reg} onChange={setReg} uploader={(f) => uploadShopFile(slug, f, "staff")} testId="staff-form-reg" />
          </div>
        </div>
        {err && <div className="text-red-600 text-sm mt-2" data-testid="staff-form-error">{err}</div>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("staff_form.cancel")}</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900" data-testid="staff-form-submit">
            <Save className="w-4 h-4 mr-1" /> {t("staff_form.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Menu tab ---------------- */
function MenuTab({ slug, onAuthErr }) {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [nameTh, setNameTh] = useState("");
  const [price, setPrice] = useState("");
  const [editing, setEditing] = useState(null);
  const load = async () => {
    try {
      const r = await shopApi(slug).get(`/shops/${slug}/services`);
      setRows(r.data.services);
    } catch (e) {
      if (e.response?.status === 401) onAuthErr?.();
      else toast.error(e.response?.data?.detail || t("admin.err.load_menu"));
    }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name || !price) return;
    try {
      await shopApi(slug).post(`/shops/${slug}/services`, {
        name,
        name_th: nameTh || null,
        price_per_60min: Number(price),
      });
      setName(""); setNameTh(""); setPrice("");
      toast.success(t("admin.ok.service_added"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("admin.err.generic")); }
  };
  const save = async (r) => {
    try {
      await shopApi(slug).put(`/shops/${slug}/services/${r.id}`, {
        name: r.name,
        name_th: r.name_th || null,
        price_per_60min: Number(r.price_per_60min),
      });
      setEditing(null);
      toast.success(t("admin.ok.updated"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("admin.err.generic")); }
  };
  const del = async (r) => {
    if (!window.confirm(t("admin.ask.delete_service"))) return;
    await shopApi(slug).delete(`/shops/${slug}/services/${r.id}`);
    load();
  };
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-serif text-2xl">{t("hub.menu")}</h2>
          <p className="text-sm text-stone-500">{t("hub.menu_desc")}</p>
        </div>
      </div>
      <div className="grid md:grid-cols-4 gap-3 mb-4">
        <Input placeholder={t("admin.menu.name_en_placeholder")} value={name} onChange={(e) => setName(e.target.value)} data-testid="menu-new-name" />
        <Input placeholder={t("admin.menu.name_th_placeholder")} value={nameTh} onChange={(e) => setNameTh(e.target.value)} data-testid="menu-new-name-th" />
        <Input type="number" min="0" placeholder={t("admin.menu.price_placeholder")} value={price} onChange={(e) => setPrice(e.target.value)} data-testid="menu-new-price" />
        <Button onClick={add} className="bg-emerald-800 hover:bg-emerald-900" data-testid="menu-add-button"><Plus className="w-4 h-4 mr-1" /> {t("admin.menu.add_service")}</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left border-b border-stone-200">
            <tr>
              <th className="py-2 pr-3 caption-label">{t("admin.col.service_en")}</th>
              <th className="py-2 pr-3 caption-label">{t("admin.col.service_th")}</th>
              <th className="py-2 pr-3 caption-label text-right">{t("admin.col.price_per_60")}</th>
              <th className="py-2 pr-3 caption-label text-right">{t("admin.col.actions")}</th>
            </tr>
          </thead>
          <tbody data-testid="menu-table-body">
            {rows.map(r => (
              <tr key={r.id} className="border-b border-stone-100" data-testid={`menu-row-${r.id}`}>
                <td className="py-2 pr-3">
                  {editing?.id === r.id
                    ? <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                    : r.name}
                </td>
                <td className="py-2 pr-3">
                  {editing?.id === r.id
                    ? <Input value={editing.name_th || ""} onChange={(e) => setEditing({ ...editing, name_th: e.target.value })} placeholder="ชื่อไทย" data-testid={`menu-edit-name-th-${r.id}`} />
                    : <span className="text-stone-600">{r.name_th || <em className="text-stone-400">—</em>}</span>}
                </td>
                <td className="py-2 pr-3 text-right">
                  {editing?.id === r.id
                    ? <Input type="number" value={editing.price_per_60min} onChange={(e) => setEditing({ ...editing, price_per_60min: e.target.value })} className="text-right" />
                    : <span className="font-mono tabular">{fmtTHBn(r.price_per_60min)}</span>}
                </td>
                <td className="py-2 pr-3 text-right">
                  {editing?.id === r.id ? (
                    <>
                      <Button size="sm" onClick={() => save(editing)} className="bg-emerald-800 hover:bg-emerald-900" data-testid={`menu-save-${r.id}`}><Save className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>{t("admin.btn.cancel")}</Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setEditing({ ...r })} data-testid={`menu-edit-${r.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => del(r)} data-testid={`menu-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-stone-500">{t("admin.menu.no_services")}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ---------------- Expense categories manager ---------------- */
function ExpenseCategoryManager({ slug, onChanged }) {
  const { t } = useLang();
  const [cats, setCats] = useState([]);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null); // {id, name}

  const load = async () => {
    try {
      const r = await shopApi(slug).get(`/shops/${slug}/expense-categories`);
      setCats(r.data.categories);
    } catch (e) { toast.error(e.response?.data?.detail || t("admin.err.load_categories")); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newName.trim()) return;
    try {
      await shopApi(slug).post(`/shops/${slug}/expense-categories`, { name: newName.trim() });
      setNewName("");
      toast.success(t("admin.ok.category_added"));
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || t("admin.err.generic")); }
  };
  const saveEdit = async () => {
    if (!editing?.name?.trim()) return;
    try {
      await shopApi(slug).put(`/shops/${slug}/expense-categories/${editing.id}`, { name: editing.name.trim() });
      setEditing(null);
      toast.success(t("admin.ok.category_renamed"));
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || t("admin.err.generic")); }
  };
  const del = async (c) => {
    if (!window.confirm(t("admin.ask.delete_category"))) return;
    try {
      await shopApi(slug).delete(`/shops/${slug}/expense-categories/${c.id}`);
      toast.success(t("admin.ok.category_removed"));
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || t("admin.err.generic")); }
  };

  return (
    <div className="rounded-lg border border-stone-200 p-4 mb-5 bg-stone-50/40" data-testid="expense-categories-manager">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif text-lg">{t("admin.exp.categories")}</h3>
        <div className="flex items-center gap-2">
          <Input
            placeholder={t("admin.exp.new_category")}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="w-48"
            data-testid="expense-category-new-name"
            maxLength={40}
            onKeyDown={(e) => e.key === "Enter" && add()}
          />
          <Button size="sm" onClick={add} className="bg-emerald-800 hover:bg-emerald-900" data-testid="expense-category-add">
            <Plus className="w-3.5 h-3.5 mr-1" /> {t("admin.btn.add")}
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="expense-category-list">
        {cats.map(c => (
          <span
            key={c.id}
            className="inline-flex items-center gap-1 rounded-full bg-white border border-stone-200 pl-3 pr-1 py-1 text-sm"
            data-testid={`expense-category-pill-${c.name}`}
          >
            {editing?.id === c.id ? (
              <>
                <input
                  autoFocus
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="bg-transparent outline-none border-b border-emerald-800 w-32 text-sm"
                  maxLength={40}
                  data-testid={`expense-category-edit-input-${c.name}`}
                  onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                />
                <button
                  onClick={saveEdit}
                  className="text-emerald-800 hover:text-emerald-950 p-1"
                  data-testid={`expense-category-save-${c.name}`}
                  title={t("admin.btn.save")}
                ><Save className="w-3.5 h-3.5" /></button>
                <button
                  onClick={() => setEditing(null)}
                  className="text-stone-400 hover:text-stone-600 p-1"
                  title={t("admin.btn.cancel")}
                >✕</button>
              </>
            ) : (
              <>
                <span>{c.name}</span>
                {c.in_use > 0 && (
                  <span className="caption-label text-stone-400 text-[10px]" title={t("admin.exp.in_use_tip").replace("{n}", c.in_use)}>
                    · {c.in_use}
                  </span>
                )}
                <button
                  onClick={() => setEditing({ id: c.id, name: c.name })}
                  className="ml-1 text-stone-500 hover:text-stone-800 p-1"
                  data-testid={`expense-category-edit-${c.name}`}
                  title={t("admin.btn.rename")}
                ><Pencil className="w-3 h-3" /></button>
                <button
                  onClick={() => del(c)}
                  disabled={c.in_use > 0}
                  className="text-red-500 hover:text-red-700 disabled:text-stone-300 disabled:cursor-not-allowed p-1"
                  data-testid={`expense-category-delete-${c.name}`}
                  title={c.in_use > 0 ? t("admin.exp.in_use_tip").replace("{n}", c.in_use) : t("admin.btn.delete")}
                ><Trash2 className="w-3 h-3" /></button>
              </>
            )}
          </span>
        ))}
        {cats.length === 0 && <div className="text-sm text-stone-500">{t("admin.exp.no_categories")}</div>}
      </div>
    </div>
  );
}

/* ---------------- Expenses tab ---------------- */
function ExpensesTab({ slug, onAuthErr }) {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState(["Other"]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [category, setCategory] = useState("Other");
  const [details, setDetails] = useState("");
  const [amount, setAmount] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const load = async () => {
    try {
      const [r, c] = await Promise.all([
        shopApi(slug).get(`/shops/${slug}/expenses`),
        shopApi(slug).get(`/shops/${slug}/expenses/categories`),
      ]);
      setRows(r.data.expenses);
      setCategories(c.data.categories);
      if (c.data.categories.length && !c.data.categories.includes(category)) {
        setCategory(c.data.categories[0]);
      }
    } catch (e) {
      if (e.response?.status === 401) onAuthErr?.();
      else toast.error(e.response?.data?.detail || t("admin.err.load_expenses"));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [reloadKey]);

  const add = async () => {
    if (!date || !category || !details || !amount) return toast.error(t("admin.err.fill_fields"));
    try {
      await shopApi(slug).post(`/shops/${slug}/expenses`, {
        date, category, details: details.slice(0, 250), amount_thb: Number(amount), receipt_path: receipt,
      });
      setDetails(""); setAmount(""); setReceipt(null);
      toast.success(t("admin.ok.expense_saved"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("admin.err.generic")); }
  };
  const del = async (r) => {
    if (!window.confirm(t("admin.ask.delete_expense"))) return;
    await shopApi(slug).delete(`/shops/${slug}/expenses/${r.id}`);
    load();
  };
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-serif text-2xl">{t("hub.expenses")}</h2>
          <p className="text-sm text-stone-500">{t("hub.expenses_desc")}</p>
        </div>
      </div>

      <ExpenseCategoryManager slug={slug} onChanged={() => setReloadKey(k => k + 1)} />

      <div className="grid md:grid-cols-6 gap-3 mb-4">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="expense-new-date" />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="h-10 rounded-md border border-stone-200 bg-white px-3 text-sm"
          data-testid="expense-new-category"
        >
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <Input placeholder={t("admin.exp.details_placeholder")} value={details} onChange={(e) => setDetails(e.target.value)} maxLength={250} className="md:col-span-2" data-testid="expense-new-details" />
        <Input type="number" min="0" step="0.01" placeholder={t("admin.exp.amount_placeholder")} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="expense-new-amount" />
        <Button onClick={add} className="bg-emerald-800 hover:bg-emerald-900" data-testid="expense-add-button"><Plus className="w-4 h-4 mr-1" />{t("admin.btn.add")}</Button>
        <div className="md:col-span-6">
          <FileUpload label={t("admin.exp.receipt_image")} value={receipt} onChange={setReceipt} uploader={(f) => uploadShopFile(slug, f, "expense")} testId="expense-new-receipt" />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left border-b border-stone-200">
            <tr>
              <th className="py-2 pr-3 caption-label">{t("admin.col.date")}</th>
              <th className="py-2 pr-3 caption-label">{t("admin.col.category")}</th>
              <th className="py-2 pr-3 caption-label">{t("admin.col.details")}</th>
              <th className="py-2 pr-3 caption-label text-right">{t("admin.col.amount")}</th>
              <th className="py-2 pr-3 caption-label">{t("admin.col.receipt")}</th>
              <th className="py-2 pr-3 caption-label text-right">{t("admin.col.actions")}</th>
            </tr>
          </thead>
          <tbody data-testid="expenses-table-body">
            {rows.map(r => (
              <tr key={r.id} className="border-b border-stone-100" data-testid={`expense-row-${r.id}`}>
                <td className="py-2 pr-3 font-mono">{r.date}</td>
                <td className="py-2 pr-3">{r.category || "Other"}</td>
                <td className="py-2 pr-3">{r.details}</td>
                <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHB(r.amount_thb)}</td>
                <td className="py-2 pr-3">{r.receipt_path ? <a href={fileUrl(r.receipt_path)} target="_blank" rel="noreferrer" className="text-emerald-800 underline text-xs">{t("admin.btn.view")}</a> : "—"}</td>
                <td className="py-2 pr-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => del(r)} data-testid={`expense-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-stone-500">{t("admin.exp.no_expenses")}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ---------------- Licences tab ---------------- */
function LicencesTab({ slug, onAuthErr }) {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState("");
  const [image, setImage] = useState(null);
  const load = async () => {
    try {
      const r = await shopApi(slug).get(`/shops/${slug}/licenses`);
      setRows(r.data.licenses);
    } catch (e) {
      if (e.response?.status === 401) onAuthErr?.();
      else toast.error(e.response?.data?.detail || "Failed to load licences");
    }
  };
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!image) return toast.error("Upload an image first");
    try {
      await shopApi(slug).post(`/shops/${slug}/licenses`, { image_path: image, note });
      setNote(""); setImage(null);
      toast.success("Licence added"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (r) => {
    if (!window.confirm("Delete this licence?")) return;
    await shopApi(slug).delete(`/shops/${slug}/licenses/${r.id}`); load();
  };
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-serif text-2xl">{t("hub.licences")}</h2>
          <p className="text-sm text-stone-500">{t("hub.licences_desc")}</p>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <div className="md:col-span-2">
          <FileUpload label="Licence image" value={image} onChange={setImage} uploader={(f) => uploadShopFile(slug, f, "licence")} testId="licence-new-image" />
        </div>
        <Input placeholder="Note / description" value={note} onChange={(e) => setNote(e.target.value)} data-testid="licence-new-note" />
      </div>
      <Button onClick={add} className="bg-emerald-800 hover:bg-emerald-900 mb-4" data-testid="licence-add-button"><Plus className="w-4 h-4 mr-1" /> Add licence</Button>
      <div className="grid md:grid-cols-2 gap-4" data-testid="licences-list">
        {rows.map(r => (
          <Card key={r.id} className="border-stone-200/80" data-testid={`licence-card-${r.id}`}>
            <CardContent className="p-4">
              <img src={fileUrl(r.image_path)} alt="" className="w-full h-48 object-cover rounded mb-3 ring-1 ring-stone-200" />
              <div className="text-sm text-stone-700">{r.note || "No note"}</div>
              <div className="mt-3">
                <Button size="sm" variant="ghost" onClick={() => del(r)} data-testid={`licence-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {rows.length === 0 && <div className="text-sm text-stone-500">No licences uploaded.</div>}
      </div>
    </>
  );
}

/* ---------------- Other Income tab ---------------- */
function OtherIncomeTab({ slug, onAuthErr }) {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [category, setCategory] = useState("girls_release_fee");
  const [workingName, setWorkingName] = useState("");
  const [amount, setAmount] = useState("");

  const load = async () => {
    try {
      const r = await shopApi(slug).get(`/shops/${slug}/other-income`);
      setRows(r.data.entries);
    } catch (e) {
      if (e.response?.status === 401) onAuthErr?.();
      else toast.error(e.response?.data?.detail || t("admin.err.load_other"));
    }
  };
  useEffect(() => { load(); }, []);

  const CATEGORY_LABELS = {
    girls_release_fee: t("hub.other.girls_release_fee"),
  };

  const add = async () => {
    if (!date || !category || !workingName.trim() || !amount) return toast.error(t("admin.err.fill_fields"));
    try {
      await shopApi(slug).post(`/shops/${slug}/other-income`, {
        date, category, working_name: workingName.trim(), amount_thb: Number(amount),
      });
      setWorkingName(""); setAmount("");
      toast.success(t("hub.other.saved"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("admin.err.generic")); }
  };

  const del = async (r) => {
    if (!window.confirm(t("admin.ask.delete_other"))) return;
    await shopApi(slug).delete(`/shops/${slug}/other-income/${r.id}`);
    load();
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-serif text-2xl">{t("hub.other_income")}</h2>
          <p className="text-sm text-stone-500">{t("hub.other_income_desc")}</p>
        </div>
      </div>
      <div className="grid md:grid-cols-5 gap-3 mb-4">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="other-new-date" />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="h-10 rounded-md border border-stone-200 bg-white px-3 text-sm"
          data-testid="other-new-category"
        >
          <option value="girls_release_fee">{CATEGORY_LABELS.girls_release_fee}</option>
        </select>
        <Input placeholder={t("input.staff_working_name")} value={workingName} onChange={(e) => setWorkingName(e.target.value)} data-testid="other-new-working-name" />
        <Input type="number" min="0" step="1" placeholder={t("admin.exp.amount_placeholder")} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="other-new-amount" />
        <Button onClick={add} className="bg-emerald-800 hover:bg-emerald-900" data-testid="other-add-button">
          <Plus className="w-4 h-4 mr-1" />{t("admin.btn.add")}
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left border-b border-stone-200">
            <tr>
              <th className="py-2 pr-3 caption-label">{t("admin.col.date")}</th>
              <th className="py-2 pr-3 caption-label">{t("admin.col.category")}</th>
              <th className="py-2 pr-3 caption-label">{t("admin.col.working_name")}</th>
              <th className="py-2 pr-3 caption-label text-right">{t("admin.col.amount")}</th>
              <th className="py-2 pr-3 caption-label text-right">{t("admin.col.actions")}</th>
            </tr>
          </thead>
          <tbody data-testid="other-income-table-body">
            {rows.map(r => (
              <tr key={r.id} className="border-b border-stone-100" data-testid={`other-row-${r.id}`}>
                <td className="py-2 pr-3 font-mono">{r.date}</td>
                <td className="py-2 pr-3">{CATEGORY_LABELS[r.category] || r.category}</td>
                <td className="py-2 pr-3">{r.working_name}</td>
                <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHBn(r.amount_thb)}</td>
                <td className="py-2 pr-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => del(r)} data-testid={`other-delete-${r.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-red-600" />
                  </Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-stone-500">{t("admin.other.no_entries")}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ---------------- Main hub ---------------- */
export default function ShopAdmin() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { t } = useLang();
  const [shop, setShop] = useState(null);
  const [tab, setTab] = useState("staff");

  const onAuthErr = React.useCallback(() => nav(`/${slug}`), [nav, slug]);
  useShopIdleLogout(slug, t("shop.idle_signout"));

  useEffect(() => {
    const sess = getShopSession(slug);
    if (!sess?.token || !["owner","manager"].includes(sess.role)) { nav(`/${slug}`); return; }
    (async () => {
      try { const r = await axios.get(`${API}/shops/by-slug/${slug}`); setShop(r.data); } catch (e) { /* header can render without shop data */ }
    })();
  }, [slug, nav]);

  return (
    <div className="min-h-screen shop-bg">
      <ShopHeader shop={shop} showSignOut />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <button onClick={() => nav(`/${slug}`)} className="caption-label flex items-center gap-1 mb-2 text-stone-500 hover:text-stone-800" data-testid="admin-back-link">
              <ChevronLeft className="w-3 h-3" /> back
            </button>
            <h1 className="font-serif text-4xl text-stone-900">{t("hub.title")}</h1>
          </div>
          <ChangeMyPin slug={slug} />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6 bg-stone-100">
            <TabsTrigger value="staff" data-testid="admin-tab-staff"><Users className="w-4 h-4 mr-1.5" />{t("hub.staff")}</TabsTrigger>
            <TabsTrigger value="menu" data-testid="admin-tab-menu"><ScrollText className="w-4 h-4 mr-1.5" />{t("hub.menu")}</TabsTrigger>
            <TabsTrigger value="other-income" data-testid="admin-tab-other-income"><Coins className="w-4 h-4 mr-1.5" />{t("hub.other_income")}</TabsTrigger>
            <TabsTrigger value="expenses" data-testid="admin-tab-expenses"><DollarSign className="w-4 h-4 mr-1.5" />{t("hub.expenses")}</TabsTrigger>
            <TabsTrigger value="licences" data-testid="admin-tab-licences"><ShieldCheck className="w-4 h-4 mr-1.5" />{t("hub.licences")}</TabsTrigger>
          </TabsList>
          <Card className="border-stone-200/80">
            <CardContent className="p-6">
              <TabsContent value="staff"><StaffTab slug={slug} onAuthErr={onAuthErr} /></TabsContent>
              <TabsContent value="menu"><MenuTab slug={slug} onAuthErr={onAuthErr} /></TabsContent>
              <TabsContent value="other-income"><OtherIncomeTab slug={slug} onAuthErr={onAuthErr} /></TabsContent>
              <TabsContent value="expenses"><ExpensesTab slug={slug} onAuthErr={onAuthErr} /></TabsContent>
              <TabsContent value="licences"><LicencesTab slug={slug} onAuthErr={onAuthErr} /></TabsContent>
            </CardContent>
          </Card>
        </Tabs>
      </main>
    </div>
  );
}
